import React from 'react';
import {
  normalizeInterventionInstanceStatut,
  isTerminalInterventionInstanceStatutRaw,
  type InterventionInstanceStatutApi,
} from '../../../../shared/constants/interventionInstanceStatus';

/** Modèle partagé interventions CTA (sidebar + synoptique + modal). */

export type { InterventionInstanceStatutApi };
export { normalizeInterventionInstanceStatut };

export interface InstanceRow {
  id: number;
  scenario_id?: number;
  /** Catégorie mission CTA (via scénario) — pour icônes carte / regroupements. */
  mission_category_id?: number | null;
  mission_category_code?: string | null;
  mission_category_nom?: string | null;
  /** Optionnel sur l’instance (lieu / type incident). */
  incident_type_id?: number | null;
  scenario_titre?: string | null;
  scenario_memo_line?: string | null;
  description_joueur?: string | null;
  numero_intervention?: string;
  /** Priorité opérationnelle définie par le joueur (modifiable tant que non terminée). */
  priorite?: number | null;
  statut?: string;
  gameplay_phase?: string | null;
  travel_estimate_sec?: number;
  adresse?: string;
  latitude?: number | null;
  longitude?: number | null;
  date_creation?: string;
  date_debut?: string | null;
  /** Renseignée quand l’instance est clôturée (succès ou échec). */
  date_fin?: string | null;
  /** Gains crédités en fin d’intervention (si renseignés côté serveur). */
  xp_gagne?: number | null;
  argent_gagne?: number | null;
  scenario_temps_min?: number | null;
  scenario_moyens_min?: number | null;
  /** 1 / true si le scénario est « exceptionnel » (rétrocompat). Tous les scénarios sont partageables via guilde. */
  scenario_is_exceptionnelle?: number | boolean | null;
  /** Message d'appel initial affiché en bannière CTA et dans le rapport d'intervention. */
  narrative_appel?: string | null;
  /** Révélations progressives débloquées selon la phase. Format : [{phase, texte}] */
  narrative_revelations?: Array<{ phase: string; texte: string }> | null;
  dispatch_total?: number;
  dispatch_sur_place?: number;
  dispatch_en_route?: number;
  /** IDs `player_vehicle` engagés sur cette instance (pour lien UI bâtiment / synoptique). */
  dispatch_player_vehicle_ids?: number[];
  all_units_on_site_at?: string | null;
  joueur_id?: number;
  user_id?: number;
}

export interface VehRow {
  id: number;
  nom?: string;
  id_status?: number;
  vehicule_type_id?: number;
}

export interface SectorVehicleRow {
  id: number;
  nom?: string | null;
  type_nom?: string | null;
  type_code?: string | null;
  batiment_id?: number | null;
  batiment_nom?: string | null;
  distance_km?: number | null;
}

/**
 * Titre + sous-titre pour la liste « engins du secteur ».
 * Évite la répétition « VLCDG · VLCDG » lorsque le nom recopié = type.
 */
export function formatSectorVehicleLines(v: SectorVehicleRow): { title: string; subtitle: string } {
  const nom = String(v.nom ?? '').trim();
  const typeNom = String(v.type_nom ?? '').trim();
  const typeCode = String(v.type_code ?? '').trim();
  const type = typeNom || typeCode;
  const sameTypeAsNom =
    type.length > 0 && nom.length > 0 && nom.toLowerCase() === type.toLowerCase();
  let title: string;
  if (sameTypeAsNom) {
    title = nom || type;
  } else if (type && nom) {
    title = `${type} · ${nom}`;
  } else {
    title = nom || type || `Véhicule ${v.id}`;
  }
  const sub: string[] = [];
  const bat = String(v.batiment_nom ?? '').trim();
  if (bat) sub.push(bat);
  const dk = v.distance_km;
  if (dk != null && Number.isFinite(Number(dk))) {
    sub.push(`${Number(dk)} km`);
  }
  return { title, subtitle: sub.join(' · ') };
}

export function DispatcherHeadsetIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M20 10c-3.5 0-6.5 2.2-7.6 5.3M27.6 15.3A8 8 0 0012 18v2.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="20" cy="17" r="4.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M10 22.5v6a2 2 0 002 2h1.5M30 22.5v6a2 2 0 01-2 2h-1.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect x="6" y="20" width="6" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <rect x="28" y="20" width="6" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M26 11l4-2v5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
    </svg>
  );
}

/**
 * Clé stable pour regrouper les interventions sur la carte : même catégorie mission → même rendu.
 * Ordre : catégorie mission (id ou code) → scénario → type d’incident → défaut.
 */
export function interventionCategoryMapKey(inv: InstanceRow): string {
  const mcId = inv.mission_category_id;
  if (mcId != null && Number.isFinite(Number(mcId)) && Number(mcId) > 0) {
    return `mc:${Number(mcId)}`;
  }
  const code = String(inv.mission_category_code ?? '')
    .trim()
    .toLowerCase();
  if (code) return `code:${code}`;
  const sid = inv.scenario_id;
  if (sid != null && Number.isFinite(Number(sid)) && Number(sid) > 0) {
    return `sc:${Number(sid)}`;
  }
  const it = inv.incident_type_id;
  if (it != null && Number.isFinite(Number(it)) && Number(it) > 0) {
    return `it:${Number(it)}`;
  }
  return 'default';
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Même clé → même variation (icône PNG commune + filtre couleur + halo). */
export function interventionMarkerVisualForCategoryKey(key: string): {
  hueRotate: number;
  ringColor: string;
} {
  if (key === 'default') {
    return { hueRotate: 0, ringColor: 'rgba(255,255,255,0.95)' };
  }
  const h = hashString(key);
  const hueRotate = h % 360;
  const ringHue = (h * 37) % 360;
  const ringColor = `hsla(${ringHue}, 82%, 58%, 0.95)`;
  return { hueRotate, ringColor };
}

export function categoryGlyph(addr: string): string {
  const a = (addr || '').toLowerCase();
  if (/feu|incendie|flamme|paille/.test(a)) return '🔥';
  if (/inond|crue|eau|pompage|fuite/.test(a)) return '💧';
  if (/police|fdo|agression|vol|violence/.test(a)) return '👮';
  if (/accident|route|collision/.test(a)) return '🚗';
  if (/samu|m[ée]dical|malaise|douleur|sds/.test(a)) return '🚑';
  if (/inconnu|rumeur/.test(a)) return '❓';
  return '⚙️';
}

function splitAddress(ad: string | undefined): { title: string; rest: string } {
  const s = (ad || '').trim();
  if (!s) return { title: '', rest: '' };
  const parts = s.split(',').map((x) => x.trim()).filter(Boolean);
  if (parts.length <= 1) return { title: s, rest: '' };
  return { title: parts[0], rest: parts.slice(1).join(', ') };
}

export function getInterventionDisplayLines(
  inv: InstanceRow,
  missionFallback: string
): { headline: string; subline: string; glyphHint: string } {
  const scenarioTitle = String(inv.scenario_titre || '').trim();
  const memoLine = String(inv.scenario_memo_line || '').trim();
  const desc = String(inv.description_joueur || '').trim();
  const { title, rest } = splitAddress(inv.adresse);
  const addrFull = String(inv.adresse || '').trim();
  const shortDesc = desc.length > 0 && desc.length <= 160 ? desc : '';
  const missionLabel = scenarioTitle || memoLine || shortDesc;

  let headline: string;
  let subline: string;

  if (missionLabel) {
    headline = missionLabel;
    subline = addrFull;
    if (headline === subline) {
      subline = rest;
    }
  } else {
    headline =
      title ||
      addrFull ||
      String(inv.numero_intervention || '').trim() ||
      `${missionFallback} #${inv.id}`;
    subline = rest;
  }

  if (!headline.trim()) {
    headline =
      String(inv.numero_intervention || '').trim() || `${missionFallback} #${inv.id}`;
  }

  const glyphHint = `${scenarioTitle} ${memoLine} ${addrFull}`;
  return { headline: headline.trim(), subline: subline.trim(), glyphHint };
}

/** Toutes les interventions sont partageables via guilde. Flag is_exceptionnelle conservé pour rétrocompat. */
export function isScenarioPartageEligible(_inv: InstanceRow): boolean {
  return true;
}

export function mergeInterventionRow(existing: InstanceRow, incoming: InstanceRow): InstanceRow {
  const out: InstanceRow = { ...existing };
  const o = out as unknown as Record<string, unknown>;
  (Object.keys(incoming) as (keyof InstanceRow)[]).forEach((key) => {
    const v = incoming[key];
    if (v !== undefined) {
      o[key as string] = v;
    }
  });
  return out;
}

export function isTerminalInterventionStatus(statut: string): boolean {
  return isTerminalInterventionInstanceStatutRaw(statut);
}

/** Regroupement UI des phases (libellés métiers : attente → prise en compte → en cours → clôturée). */
export type InterventionPhaseUiKey = 'wait' | 'travel' | 'site' | 'done' | 'other';

export function getInterventionPhaseUiKey(inv: InstanceRow): InterventionPhaseUiKey {
  const api = normalizeInterventionInstanceStatut(inv.statut || '');
  if (api === 'TERMINE' || api === 'ANNULE') return 'done';

  const ph = String(inv.gameplay_phase || '').toUpperCase();
  if (ph === 'ON_SITE') return 'site';
  if (
    ph === 'TRAVELING' ||
    ph === 'HOSPITAL_TRANSPORT' ||
    ph === 'HOSPITAL_ARRIVAL' ||
    ph === 'RETURNING'
  )
    return 'travel';
  if (ph === 'ASSIGNED') return 'wait';
  if (ph === 'WAITING') return 'wait';
  if (api === 'EN_ATTENTE') return 'wait';
  if (api === 'EN_COURS') return 'travel';
  return 'other';
}

/** Clé i18n (Map.json → interventionSidebar.*). */
export function interventionPhaseI18nKey(k: InterventionPhaseUiKey): string {
  switch (k) {
    case 'wait':
      return 'interventionSidebar.phaseLabelWait';
    case 'travel':
      return 'interventionSidebar.phaseLabelTravel';
    case 'site':
      return 'interventionSidebar.phaseLabelSite';
    case 'done':
      return 'interventionSidebar.phaseLabelDone';
    default:
      return 'interventionSidebar.phaseLabelOther';
  }
}

/** Bandeau / pastilles UI : couleurs par phase (évite le rouge « alarme » sur toutes les inter). */
export function getInterventionPhaseAccent(phase: InterventionPhaseUiKey): {
  topBorder: string;
  kicker: string;
  headset: string;
  glyphBox: string;
} {
  switch (phase) {
    case 'wait':
      return {
        topBorder: 'border-amber-500',
        kicker: 'text-amber-400/90',
        headset: 'text-amber-100 drop-shadow-[0_0_12px_rgba(245,158,11,0.35)]',
        glyphBox: 'border-amber-500/75 bg-amber-950/90',
      };
    case 'travel':
      return {
        topBorder: 'border-sky-500',
        kicker: 'text-sky-400/90',
        headset: 'text-sky-100 drop-shadow-[0_0_12px_rgba(14,165,233,0.35)]',
        glyphBox: 'border-sky-500/75 bg-sky-950/90',
      };
    case 'site':
      return {
        topBorder: 'border-emerald-500',
        kicker: 'text-emerald-400/90',
        headset: 'text-emerald-100 drop-shadow-[0_0_12px_rgba(16,185,129,0.35)]',
        glyphBox: 'border-emerald-500/75 bg-emerald-950/90',
      };
    case 'done':
      return {
        topBorder: 'border-zinc-500',
        kicker: 'text-zinc-400/90',
        headset: 'text-zinc-200 drop-shadow-[0_0_12px_rgba(113,113,122,0.25)]',
        glyphBox: 'border-zinc-500/75 bg-zinc-800/90',
      };
    default:
      return {
        topBorder: 'border-violet-500',
        kicker: 'text-violet-400/90',
        headset: 'text-violet-100 drop-shadow-[0_0_12px_rgba(139,92,246,0.35)]',
        glyphBox: 'border-violet-500/75 bg-violet-950/90',
      };
  }
}

const PHASE_TRACK_STEPS = [
  { current: 'bg-amber-500/90', past: 'bg-amber-500/40' },
  { current: 'bg-sky-500/90', past: 'bg-sky-500/40' },
  { current: 'bg-emerald-500/90', past: 'bg-emerald-500/40' },
  { current: 'bg-zinc-500/85', past: 'bg-zinc-600/45' },
] as const;

function phaseTrackIndex(phase: InterventionPhaseUiKey): number {
  switch (phase) {
    case 'wait':
      return 0;
    case 'travel':
      return 1;
    case 'site':
      return 2;
    case 'done':
      return 3;
    default:
      return -1;
  }
}

/** Avancement discret (4 étapes) à la place d’une barre en dégradé. */
export function InterventionPhaseTrack({
  phase,
  dense = false,
  className = '',
}: {
  phase: InterventionPhaseUiKey;
  dense?: boolean;
  className?: string;
}) {
  const idx = phaseTrackIndex(phase);
  const h = dense ? 'h-1.5' : 'h-2';

  return (
    <div className={`flex min-w-0 flex-1 items-center gap-1 ${className}`} aria-hidden>
      {[0, 1, 2, 3].map((i) => {
        let segment = 'bg-zinc-800/90';
        if (phase === 'other') {
          if (i === 0) segment = 'bg-violet-500/88';
        } else if (idx >= 0) {
          const pal = PHASE_TRACK_STEPS[i];
          if (i < idx) segment = pal.past;
          else if (i === idx) segment = pal.current;
        }
        return (
          <div
            key={i}
            className={`${h} min-w-0 flex-1 rounded-full ${segment} ring-1 ring-black/25`}
          />
        );
      })}
    </div>
  );
}

export function effectiveMoyensRequired(inv: InstanceRow): number {
  const m = Number(inv.scenario_moyens_min ?? 0);
  return Number.isFinite(m) && m > 0 ? m : 1;
}

export function effectiveTempsSurPlaceMinutes(inv: InstanceRow): number {
  const n = Number(inv.scenario_temps_min);
  if (!Number.isFinite(n) || n < 0) return 15;
  return n;
}

export function formatDurationMmSs(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
